/**
 * The firm's diary (S-A61) — hearings, appointments, meetings and ordinary deadlines.
 *
 * ===========================================================================
 * CRITICAL DATES ARE NOT ON THIS SCREEN, AND THAT IS THE POINT
 * ===========================================================================
 * They are a RISK REGISTER, not a diary. A limitation date drawn among meetings invites
 * the reading that missing it is like missing a meeting; it is not. It is a potential
 * negligence exposure, it requires independent verification by a second person, it
 * cannot be moved or deleted, and it survives the matter closing. The backend keeps
 * them in a separate table with a different set of verbs for exactly that reason.
 *
 * So this screen LINKS to the register and does not reproduce any of it — no count, no
 * badge, no "upcoming dates" strip that would quietly become a second, unverified copy
 * of the firm's risk position. And nothing about a critical date is ever shown to a
 * client on any surface, which is why it must not leak into a view whose entries can be
 * marked client-visible.
 * ===========================================================================
 *
 * TWO VIEWS OVER ONE QUERY. The month grid is for spotting shape and clashes; the list
 * is the fallback and is the one that survives a busy month, a screen reader and a
 * narrow window. They read the same records, so they can never disagree.
 *
 * Filters are SERVER-SIDE and allowlisted (`from`, `to`, `case`, `assignee`,
 * `event_type`, `status`). The cursor is reset whenever one changes, or the new filter
 * is applied at the old position.
 */

import {
  STAFF,
  qk,
  usePaginatedList,
  type CalendarEvent,
  type CaseListItem,
  type FirmMember,
} from '@legalcrm/api';
import {
  Badge,
  Button,
  Select,
  Table,
  cn,
  type Column,
} from '@legalcrm/ui';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Can } from '@/auth/permissions';
import { formatDateTime, humanise } from '@/lib/format';
import { PageHeader } from '@/shell/PageHeader';

import { EventDrawer } from './EventDrawer';
import { MonthGrid } from './MonthGrid';
import { NewEventModal } from './NewEventModal';
import {
  EVENT_STATUS_FILTER_OPTIONS,
  EVENT_TYPE_FILTER_OPTIONS,
  addMonths,
  gridRange,
  buildMonthGrid,
  localDayKey,
  monthLabel,
  type GridDay,
} from './calendarMonth';

/** One page at the maximum size. See `truncated` below for what happens beyond it. */
const PAGE_LIMIT = 100;

export function CalendarPage() {
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [view, setView] = useState<'month' | 'list'>('month');
  const [caseId, setCaseId] = useState('');
  const [attendee, setAttendee] = useState('');
  const [eventType, setEventType] = useState('');
  const [status, setStatus] = useState('');

  const [selected, setSelected] = useState<CalendarEvent | null>(null);
  const [creatingOn, setCreatingOn] = useState<string | null>(null);

  const range = useMemo(() => gridRange(anchor), [anchor]);
  const days = useMemo(() => buildMonthGrid(anchor), [anchor]);

  const list = usePaginatedList<CalendarEvent>(
    qk.list('calendar-events', { month: range.from }),
    STAFF.calendarEvents,
    {
      from: range.from,
      to: range.to,
      case: caseId || undefined,
      // `assignee` filters on attendees. `me` is resolved server-side from the session,
      // so the frontend never has to know its own user id to ask "what am I in?".
      assignee: attendee || undefined,
      event_type: eventType || undefined,
      status: status || undefined,
      limit: PAGE_LIMIT,
    },
  );

  const { resetCursor } = list;
  useEffect(() => {
    resetCursor();
  }, [range.from, caseId, attendee, eventType, status, resetCursor]);

  /** Pickers, and the lookup that turns an attendee id back into a name. */
  const cases = usePaginatedList<CaseListItem>(qk.list('cases', { picker: true }), STAFF.cases, {
    limit: PAGE_LIMIT,
  });
  const members = usePaginatedList<FirmMember>(qk.list('users', { picker: true }), STAFF.users, {
    limit: PAGE_LIMIT,
  });

  const eventsByDay = useMemo(() => {
    const byDay = new Map<string, CalendarEvent[]>();
    for (const event of list.items) {
      const key = localDayKey(new Date(event.starts_at));
      const bucket = byDay.get(key);
      if (bucket) bucket.push(event);
      else byDay.set(key, [event]);
    }
    for (const bucket of byDay.values()) {
      bucket.sort((a, b) => a.starts_at.localeCompare(b.starts_at));
    }
    return byDay;
  }, [list.items]);

  /**
   * A month with more entries than one page holds. Said plainly rather than shown as a
   * grid that is quietly missing court dates — the list view paginates properly, so it
   * is where somebody is sent.
   */
  const truncated = view === 'month' && list.hasNext;

  const columns: Column<CalendarEvent>[] = [
    {
      key: 'starts_at',
      header: 'When',
      width: 'w-56',
      render: (row) => (
        <div>
          <p className="tabular-nums text-ink">{formatDateTime(row.starts_at)}</p>
          {row.all_day && <p className="text-xs text-ink-muted">All day</p>}
        </div>
      ),
    },
    {
      key: 'title',
      header: 'Entry',
      render: (row) => (
        <div className="min-w-0">
          <p
            className={cn(
              'font-medium',
              row.status === 'cancelled' ? 'text-ink-subtle line-through' : 'text-ink',
            )}
          >
            {row.title}
          </p>
          <p className="text-xs text-ink-muted">
            {humanise(row.event_type)}
            {row.court_name && ` · ${row.court_name}`}
            {row.location && ` · ${row.location}`}
          </p>
        </div>
      ),
    },
    {
      key: 'case',
      header: 'Matter',
      hideOnMobile: true,
      render: (row) =>
        row.case ? (
          <Link
            to={`/cases/${row.case}`}
            onClick={(event) => event.stopPropagation()}
            className="text-primary hover:underline"
          >
            Open matter
          </Link>
        ) : (
          <span className="text-ink-subtle">—</span>
        ),
    },
    {
      key: 'visibility',
      header: 'Visibility',
      render: (row) => (
        // Text, not colour. A reader skimming forty rows must still be able to tell.
        <Badge tone={row.visibility === 'internal' ? 'internal' : 'client-facing'} size="sm">
          {row.visibility === 'internal' ? 'Internal' : 'Client sees this'}
        </Badge>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      align: 'right',
      render: (row) => (
        <Badge tone={row.status === 'cancelled' ? 'neutral' : 'info'} size="sm">
          {humanise(row.status)}
        </Badge>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Calendar"
        description="The firm's diary. Hearings, appointments, meetings and ordinary deadlines."
        actions={
          <>
            {/*
              The register, linked and not duplicated. The framing travels with the
              link, because a bare "Critical dates" button beside a diary is exactly
              the equivalence this separation exists to prevent.
            */}
            <Can module="critical_date" action="view">
              <Button variant="secondary" onClick={() => navigate('/critical-dates')}>
                Critical dates
              </Button>
            </Can>
            <Can module="calendar" action="create">
              <Button variant="primary" onClick={() => setCreatingOn(localDayKey(new Date()))}>
                New entry
              </Button>
            </Can>
          </>
        }
      />

      <Can module="critical_date" action="view">
        <p className="mb-4 rounded border bg-surface-sunken px-3 py-2 text-sm text-ink-muted">
          Critical dates are deliberately not on this calendar. They are the firm&apos;s risk
          register — verified by a second person, never moved, never deleted, and never
          shown to a client on any surface — and they live on{' '}
          <Link to="/critical-dates" className="text-primary hover:underline">
            their own screen
          </Link>
          .
        </p>
      </Can>

      <div className="mb-3 flex flex-wrap items-end gap-2">
        <div className="flex items-center gap-1">
          <Button variant="secondary" size="sm" onClick={() => setAnchor(addMonths(anchor, -1))}>
            Previous
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              const now = new Date();
              setAnchor(new Date(now.getFullYear(), now.getMonth(), 1));
            }}
          >
            Today
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setAnchor(addMonths(anchor, 1))}>
            Next
          </Button>
        </div>

        <h2 className="min-w-[10rem] text-lg font-semibold text-ink">{monthLabel(anchor)}</h2>

        <div className="ml-auto flex items-center gap-1">
          <Button
            variant={view === 'month' ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setView('month')}
            aria-pressed={view === 'month'}
          >
            Month
          </Button>
          <Button
            variant={view === 'list' ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setView('list')}
            aria-pressed={view === 'list'}
          >
            List
          </Button>
        </div>
      </div>

      <div className="mb-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <Select
          label="Matter"
          hideLabel
          options={[
            { value: '', label: 'Any matter' },
            ...cases.items.map((record) => ({
              value: record.id,
              label: `${record.case_number} — ${record.title}`,
            })),
          ]}
          value={caseId}
          onChange={(event) => setCaseId(event.target.value)}
        />
        <Select
          label="Attendee"
          hideLabel
          options={[
            { value: '', label: 'Anyone attending' },
            { value: 'me', label: 'Only what I am attending' },
            ...members.items.map((member) => ({
              // The USER id, not the membership id. `EventAttendee.user` is the former.
              value: member.user,
              label: member.full_name || member.email,
            })),
          ]}
          value={attendee}
          onChange={(event) => setAttendee(event.target.value)}
        />
        <Select
          label="Kind"
          hideLabel
          options={EVENT_TYPE_FILTER_OPTIONS}
          value={eventType}
          onChange={(event) => setEventType(event.target.value)}
        />
        <Select
          label="Status"
          hideLabel
          options={EVENT_STATUS_FILTER_OPTIONS}
          value={status}
          onChange={(event) => setStatus(event.target.value)}
        />
      </div>

      {truncated && (
        <p className="mb-3 rounded border border-warning/30 bg-warning-subtle px-3 py-2 text-sm text-ink">
          More than {PAGE_LIMIT} entries fall in this view, so the grid is not showing all
          of them. Use the list, which pages properly, or narrow the filters.
        </p>
      )}

      {list.error && !list.isLoading ? (
        // One error treatment for both views. `ErrorState` inside `Table` applies the
        // 404 rule; reuse it rather than writing a second set of words here.
        <Table
          caption="Diary"
          columns={columns}
          rows={[]}
          rowKey={(row) => row.id}
          error={list.error}
          onRetry={() => void list.refetch()}
        />
      ) : view === 'month' ? (
        <MonthGrid
          days={days}
          eventsByDay={eventsByDay}
          onSelect={setSelected}
          onSelectDay={(day: GridDay) => setCreatingOn(day.key)}
        />
      ) : (
        <Table
          caption="Diary"
          columns={columns}
          rows={list.items}
          rowKey={(row) => row.id}
          loading={list.isLoading}
          onRowClick={(row) => setSelected(row)}
          emptyTitle="Nothing in the diary"
          emptyDescription="Nothing matches these filters in this month."
          pagination={{
            hasNext: list.hasNext,
            hasPrevious: list.hasPrevious,
            onNext: list.goNext,
            onPrevious: list.goPrevious,
            summary: `${list.items.length} shown`,
          }}
        />
      )}

      {selected && (
        <EventDrawer
          // Re-read from the list so the drawer reflects a completed or cancelled
          // entry after its own mutation invalidated the query.
          event={list.items.find((row) => row.id === selected.id) ?? selected}
          onClose={() => setSelected(null)}
          cases={cases.items}
          members={members.items}
        />
      )}

      {creatingOn !== null && (
        <NewEventModal
          defaultDay={creatingOn}
          onClose={() => setCreatingOn(null)}
          cases={cases.items}
          members={members.items}
        />
      )}
    </>
  );
}
